class FloatingAddToCart extends HTMLElement {
  constructor() {
    super();
    this.productForm = null;
    this.quantityInput = null;
    this.submitButton = null;
    this.closeButton = null;
    this.isVisible = false;
    this.mainAddToCartBtn = null;
    this.mainQuantityInput = null;
    this._syncing = false;
  }

  connectedCallback() {
    this.productForm = document.getElementById(this.querySelector('.floating-add-to-cart__submit').dataset.productFormId);
    this.productId = this.dataset.productId;
    this.sessionIdentifier = `floating-cart-closed-${this.productId}`
    this.quantityInput = this.querySelector('[data-floating-qty-input]');
    this.submitButton = this.querySelector('.floating-add-to-cart__submit');
    this.closeButton = this.querySelector('.floating-add-to-cart__close');
    this.mainAddToCartBtn = document.querySelector('[data-main-add-to-cart-btn]');
    this.mainQuantityInput = this.productForm ? this.productForm.querySelector('input[name="quantity"]') : null;

    this.setupEventListeners();
    this.setupScrollVisibility();
  }

  setupEventListeners() {
    // Close button functionality
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.hide();
        // Store in session storage to prevent showing again for this session
        sessionStorage.setItem(this.sessionIdentifier, 'true');
      });
    }

    // Helper to sync values and dispatch events
    const syncValue = (fromInput, toInput) => {
      if (!fromInput || !toInput) return;
      if (toInput.value !== fromInput.value) {
        this._syncing = true;
        toInput.value = fromInput.value;
        toInput.dispatchEvent(new Event('input', { bubbles: true }));
        toInput.dispatchEvent(new Event('change', { bubbles: true }));
        this._syncing = false;
      }
    };

    // Floating -> Main
    if (this.quantityInput && this.mainQuantityInput) {
      // Listen for input and change
      this.quantityInput.addEventListener('input', () => {
        if (!this._syncing) syncValue(this.quantityInput, this.mainQuantityInput);
      });
      this.quantityInput.addEventListener('change', () => {
        if (!this._syncing) syncValue(this.quantityInput, this.mainQuantityInput);
      });
      // Listen for plus/minus button clicks
      const floatingQtyWrapper = this.quantityInput.closest('quantity-input');
      if (floatingQtyWrapper) {
        floatingQtyWrapper.addEventListener('click', (e) => {
          if (e.target.closest('button[name="plus"],button[name="minus"]')) {
            setTimeout(() => {
              if (!this._syncing) syncValue(this.quantityInput, this.mainQuantityInput);
            }, 0);
          }
        });
      }
    }

    // Main -> Floating
    if (this.mainQuantityInput && this.quantityInput) {
      this.mainQuantityInput.addEventListener('input', () => {
        if (!this._syncing) syncValue(this.mainQuantityInput, this.quantityInput);
      });
      this.mainQuantityInput.addEventListener('change', () => {
        if (!this._syncing) syncValue(this.mainQuantityInput, this.quantityInput);
      });
      // Listen for plus/minus button clicks
      const mainQtyWrapper = this.mainQuantityInput.closest('quantity-input');
      if (mainQtyWrapper) {
        mainQtyWrapper.addEventListener('click', (e) => {
          if (e.target.closest('button[name="plus"],button[name="minus"]')) {
            setTimeout(() => {
              if (!this._syncing) syncValue(this.mainQuantityInput, this.quantityInput);
            }, 0);
          }
        });
      }
    }

    // Handle add to cart button click
    if (this.submitButton && this.mainAddToCartBtn && this.mainQuantityInput) {
      this.submitButton.addEventListener('click', (event) => {
        event.preventDefault();
        // Set main form quantity to floating bar's value
        if (this.mainQuantityInput.value !== this.quantityInput.value) {
          this.mainQuantityInput.value = this.quantityInput.value;
          this.mainQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.mainAddToCartBtn.click();
      });
    }

    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      this.updateFloatingCart(event.detail);
    });

    // Listen for product form updates
    document.addEventListener('product:updated', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateFloatingCart(event.detail.variant);
      }
    });

    // Listen for cart drawer open/close
    document.addEventListener('cart-drawer:open', () => {
      this.hide();
    });
    document.addEventListener('cart-drawer:close', () => {
      this.checkVisibility();
    });
  }

  setupScrollVisibility() {
    if (!this.mainAddToCartBtn) return;
    window.addEventListener('scroll', () => this.checkVisibility());
    window.addEventListener('resize', () => this.checkVisibility());
    this.checkVisibility();
  }

  checkVisibility() {
    if (!this.mainAddToCartBtn) return;
    
    // Check if user has closed the floating cart in this session
    const isClosed = sessionStorage.getItem(this.sessionIdentifier) === 'true';
    if (isClosed) return;
    
    const rect = this.mainAddToCartBtn.getBoundingClientRect();
    // Only show if the button is fully above the viewport
    if (rect.bottom < 0) {
      this.show();
      document.body.classList.add('floating-cart-active');
    } else {
      this.hide();
      document.body.classList.remove('floating-cart-active');
    }
  }

  updateFloatingCart(variant) {
    // Update price
    const priceElement = this.querySelector('.floating-add-to-cart__price');
    if (priceElement && variant.price) {
      // Update price display - this would need to be implemented based on your price rendering
      // (left as a placeholder)
    }
    // Update availability
    if (this.submitButton) {
      const isAvailable = variant.available && !variant.quantity_rule_soldout;
      this.submitButton.disabled = !isAvailable;
      const buttonText = this.submitButton.querySelector('span');
      if (buttonText) {
        buttonText.textContent = isAvailable
          ? window.variantStrings?.addToCart || 'Add to cart'
          : window.variantStrings?.soldOut || 'Sold out';
      }
    }
    // Update image if variant has different image
    if (variant.featured_image) {
      const imageElement = this.querySelector('.floating-add-to-cart__image img');
      if (imageElement) {
        imageElement.src = variant.featured_image.src;
        imageElement.alt = variant.featured_image.alt || '';
      }
    }
    // Update quantity min/max/step
    if (this.quantityInput) {
      if (variant.quantity_rule) {
        this.quantityInput.min = variant.quantity_rule.min || 1;
        this.quantityInput.max = variant.quantity_rule.max || '';
        this.quantityInput.step = variant.quantity_rule.increment || 1;
      }
    }
  }

  show() {
    if (!this.isVisible) {
      this.classList.add('floating-add-to-cart--visible');
      this.isVisible = true;
    }
  }

  hide() {
    if (this.isVisible) {
      this.classList.remove('floating-add-to-cart--visible');
      this.isVisible = false;
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', () => this.checkVisibility());
    window.removeEventListener('resize', () => this.checkVisibility());
  }
}

customElements.define('floating-add-to-cart', FloatingAddToCart); 